import api from './api';

export interface MarketingBroadcast {
  id: string;
  title: string;
  message: string;
  scheduledAt: string;
  status: 'SCHEDULED' | 'SENT' | 'CANCELLED' | 'FAILED';
  type: 'MESSAGE' | 'BANNER';
  targetAudience: 'ALL_USERS' | 'SPECIFIC_USERS';
  targetUserIds?: string[];
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  sentAt?: string;
  failureReason?: string;
  recipientCount?: number;
  successCount?: number;
  failureCount?: number;
}

export interface CreateBroadcastRequest {
  title: string;
  message: string;
  scheduledAt: string;
  type?: 'MESSAGE' | 'BANNER';
  targetAudience?: 'ALL_USERS' | 'SPECIFIC_USERS';
  targetUserIds?: string[];
}

export interface UpdateBroadcastRequest {
  title?: string;
  message?: string;
  scheduledAt?: string;
  type?: 'MESSAGE' | 'BANNER';
  targetAudience?: 'ALL_USERS' | 'SPECIFIC_USERS';
  targetUserIds?: string[];
}

export interface PaginationParams {
  limit?: number;
  lastEvaluatedKey?: string;
}

export interface BroadcastsResponse {
  success: boolean;
  data: MarketingBroadcast[];
  pagination?: {
    hasMore: boolean;
    lastEvaluatedKey?: string;
  };
}

export interface BroadcastResponse {
  success: boolean;
  data: MarketingBroadcast;
}

export interface SendBroadcastResult {
  broadcastId: string;
  recipientCount: number;
  successCount: number;
  failureCount: number;
  sentAt: string;
}

export const marketingService = {
  /**
   * Get all broadcasts with optional status filter and pagination
   */
  getBroadcasts: async (params?: { status?: string } & PaginationParams): Promise<BroadcastsResponse> => {
    const response = await api.get('/admins/marketing/broadcasts', { params });
    return response.data;
  },

  /**
   * Get broadcast by ID
   */
  getBroadcastById: async (id: string): Promise<BroadcastResponse> => {
    const response = await api.get(`/admins/marketing/broadcasts/${id}`);
    return response.data;
  },

  /**
   * Create new broadcast
   */
  createBroadcast: async (data: CreateBroadcastRequest): Promise<BroadcastResponse> => {
    const response = await api.post('/admins/marketing/broadcasts', data);
    return response.data;
  },

  /**
   * Update broadcast
   */
  updateBroadcast: async (id: string, data: UpdateBroadcastRequest): Promise<BroadcastResponse> => {
    const response = await api.put(`/admins/marketing/broadcasts/${id}`, data);
    return response.data;
  },

  /**
   * Delete broadcast
   */
  deleteBroadcast: async (id: string): Promise<{ success: boolean; message: string }> => {
    const response = await api.delete(`/admins/marketing/broadcasts/${id}`);
    return response.data;
  },

  /**
   * Send broadcast immediately
   */
  sendBroadcastNow: async (id: string): Promise<{ success: boolean; data: SendBroadcastResult }> => {
    const response = await api.post(`/admins/marketing/broadcasts/${id}/send`);
    return response.data;
  },

  /**
   * Duplicate broadcast
   */
  duplicateBroadcast: async (id: string): Promise<BroadcastResponse> => {
    const response = await api.post(`/admins/marketing/broadcasts/${id}/duplicate`);
    return response.data;
  },

  /**
   * Cancel scheduled broadcast
   */
  cancelBroadcast: async (id: string): Promise<BroadcastResponse> => {
    const response = await api.post(`/admins/marketing/broadcasts/${id}/cancel`);
    return response.data;
  },
};
